import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import MediaEmbed from '@/components/ui/MediaEmbed'
import TagPill from '@/components/ui/TagPill'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { YouTubeVideo } from '@/types'
import * as api from '@/lib/api'

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const hrs = Math.floor(mins / 60)
  if (hrs > 0) {
    return `${hrs}h ${mins % 60}m`
  }
  return `${mins}m`
}

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [video, setVideo] = useState<YouTubeVideo | null>(null)

  // SEO
  useSEO({
    title: video?.title,
    description: video?.summary?.slice(0, 160),
    image: video?.thumbnail_url,
    url: video ? `https://thearchivenetwork.com/video/${video.id}` : undefined,
    type: 'article',
  })

  useEffect(() => {
    async function loadData() {
      if (!id) return
      try {
        const data = await api.getYouTubeVideo(id)
        setVideo(data)
      } catch (error) {
        console.error('Failed to load video:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!video) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-archive-cream">Video Not Found</h1>
          <Link to="/conversations" className="mt-4 btn-secondary inline-block">
            Browse Conversations
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-archive-muted">
            <li><Link to="/conversations" className="hover:text-archive-gold">Conversations</Link></li>
            <li>/</li>
            <li className="text-archive-text line-clamp-1">{video.title}</li>
          </ol>
        </nav>

        {/* Video Player */}
        <div className="mb-8">
          <MediaEmbed
            type="youtube"
            videoId={video.youtube_video_id}
            title={video.title}
          />
        </div>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <TagPill label={video.archive_key} variant="archive" size="md" />
            <span className="text-archive-muted">
              {new Date(video.published_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <span className="text-archive-muted">
              {formatDuration(video.duration_sec)}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-archive-cream">
            {video.title}
          </h1>
        </header>

        {/* Summary */}
        <div className="prose prose-invert max-w-none">
          <h2 className="font-display text-xl text-archive-cream mb-4">About This Video</h2>
          <p className="text-archive-text leading-relaxed whitespace-pre-wrap">
            {video.summary}
          </p>
        </div>

        {/* Related Themes */}
        {video.related_theme_slugs.length > 0 && (
          <div className="mt-8 pt-8 border-t border-archive-stone/50">
            <h3 className="font-display text-lg text-archive-cream mb-4">Related Themes</h3>
            <div className="flex flex-wrap gap-2">
              {video.related_theme_slugs.map((slug) => (
                <TagPill
                  key={slug}
                  label={slug.replace(/-/g, ' ')}
                  linkTo={`/themes/${slug}`}
                  variant="theme"
                  size="md"
                />
              ))}
            </div>
          </div>
        )}

        {/* Watch on YouTube */}
        <div className="mt-8 pt-8 border-t border-archive-stone/50">
          <a
            href={video.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            Watch on YouTube
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}
